// src/utils/money.model.ts
var MONEY_DECIMALS = 1;
var ROUND_FACTOR = 10 ** MONEY_DECIMALS;
function roundMoney(value) {
  if (!Number.isFinite(value)) {
    return 0;
  }
  const scaled = value * ROUND_FACTOR;
  const roundedScaled = scaled >= 0 ? Math.floor(scaled + 0.5) : Math.ceil(scaled - 0.5);
  return roundedScaled / ROUND_FACTOR;
}

// src/pos/ordering.model.ts
var QUICK_CHARGE_CODE_PREFIX = "QC_";
var KIOSK_DINE_IN_CODE_PREFIX = "KI_";
function isQuickChargeOrderCode(orderCode) {
  return orderCode?.startsWith(QUICK_CHARGE_CODE_PREFIX) ?? false;
}
function isKioskDineInOrderCode(orderCode) {
  return orderCode?.startsWith(KIOSK_DINE_IN_CODE_PREFIX) ?? false;
}
var WOLT_CODE_PREFIX = "WO_";
var AGGREGATOR_CODE_PREFIXES = [WOLT_CODE_PREFIX];
function isAggregatorOrderCode(orderCode) {
  return AGGREGATOR_CODE_PREFIXES.some((prefix) => orderCode?.startsWith(prefix) ?? false);
}
var AGGREGATOR_ITEM_NOTE_PREFIX = "\u05E4\u05E8\u05D9\u05D8 \u05E2\u05E8\u05D5\u05E5:";
var AGGREGATOR_ITEM_NAME_MAX_CHARS = 80;
function buildAggregatorItemNote(itemName, optionNote) {
  const name = itemName.trim().replace(/\s+/g, " ").slice(0, AGGREGATOR_ITEM_NAME_MAX_CHARS);
  const options = optionNote?.trim();
  const head = `${AGGREGATOR_ITEM_NOTE_PREFIX} ${name}`;
  return options ? [head, options].join("\n") : head;
}
function aggregatorItemNoteName(note) {
  if (typeof note !== "string" || !note.startsWith(AGGREGATOR_ITEM_NOTE_PREFIX)) {
    return null;
  }
  const name = note.slice(AGGREGATOR_ITEM_NOTE_PREFIX.length).split("\n")[0].trim();
  return name.length > 0 ? name : null;
}
function takeAwayCloseActionGroupId(orderId) {
  return `ta-close-${orderId}`;
}
var TAKE_AWAY_CHANNEL_LABEL_BY_PREFIX = {
  [WOLT_CODE_PREFIX]: "\u05D5\u05D5\u05DC\u05D8"
};
function takeAwayChannelLabel(orderCode) {
  if (!orderCode)
    return null;
  const prefix = Object.keys(TAKE_AWAY_CHANNEL_LABEL_BY_PREFIX).find((entry) => orderCode.startsWith(entry));
  return prefix ? TAKE_AWAY_CHANNEL_LABEL_BY_PREFIX[prefix] : null;
}

// src/pos/min-order.model.ts
var MIN_ORDER_COMPLETION_NOTE_PREFIX = "\u05D4\u05E9\u05DC\u05DE\u05D4 \u05DC\u05DE\u05D9\u05E0\u05D9\u05DE\u05D5\u05DD";
var MIN_ORDER_COMPLETION_LABEL = "\u05D4\u05E9\u05DC\u05DE\u05EA \u05DE\u05D9\u05E0\u05D9\u05DE\u05D5\u05DD \u05D4\u05D6\u05DE\u05E0\u05D4";
function isMinOrderCompletionNote(note) {
  return typeof note === "string" && note.startsWith(MIN_ORDER_COMPLETION_NOTE_PREFIX);
}
function resolveEffectiveMinimumOrder(config, dinersCount) {
  if (config.minimumOrderPerDiner > 0) {
    const diners = Number.isFinite(dinersCount) && dinersCount > 0 ? Math.floor(dinersCount) : 0;
    return roundMoney(config.minimumOrderPerDiner * diners);
  }
  return config.minimumOrder > 0 ? roundMoney(config.minimumOrder) : 0;
}

// src/pos/cash-alterations.model.ts
import { z } from "zod";
var CASH_ALTERATION_ACTIONS = ["entry-cash", "in", "out"];
var cashAlterationActionSchema = z.enum(CASH_ALTERATION_ACTIONS);

// src/pos/credit-payments.model.ts
var FAILED_CREDIT_RECOVERY_DELAY_MS = 9e3;

// src/pos/tips.model.ts
function resolveTip(mode, value, base) {
  if (mode == null || value == null) {
    return 0;
  }
  if (mode === "value") {
    return roundMoney(value);
  }
  if (value <= 0) {
    return 0;
  }
  return roundMoney(base * value / 100);
}
function allocateGlobalTipShare(remainingTip, lineAmount, remainingItems, isClosingLine) {
  if (remainingTip <= 0) {
    return 0;
  }
  if (isClosingLine) {
    return roundMoney(Math.max(0, remainingTip));
  }
  if (remainingItems <= 0) {
    return 0;
  }
  return roundMoney(remainingTip * lineAmount / remainingItems);
}
function allocateProportionalTip(total, lineAmounts) {
  let remainingTip = total;
  let remainingItems = roundMoney(lineAmounts.reduce((sum, amount) => sum + amount, 0));
  return lineAmounts.map((amount, index) => {
    const isClosingLine = index === lineAmounts.length - 1;
    const share = allocateGlobalTipShare(remainingTip, amount, remainingItems, isClosingLine);
    remainingTip = roundMoney(remainingTip - share);
    remainingItems = roundMoney(remainingItems - amount);
    return share;
  });
}
function isEqualSplit(lineAmounts) {
  if (lineAmounts.length < 2) {
    return true;
  }
  return roundMoney(Math.max(...lineAmounts) - Math.min(...lineAmounts)) <= 0.1;
}
function allocateEvenTotalTip(total, lineAmounts, itemsTotal) {
  const n = lineAmounts.length;
  const grandTenths = Math.round(roundMoney(itemsTotal + total) * 10);
  const baseTenths = Math.floor(grandTenths / n);
  const extra = grandTenths - baseTenths * n;
  const shares = lineAmounts.map((amount, index) => {
    const targetTenths = baseTenths + (index >= n - extra ? 1 : 0);
    return roundMoney(targetTenths / 10 - amount);
  });
  return shares.some((share) => share < 0) ? null : shares;
}
function allocateGlobalTip(globalTip, lineAmounts) {
  const total = roundMoney(globalTip);
  const itemsTotal = roundMoney(lineAmounts.reduce((sum, amount) => sum + amount, 0));
  const allZeroLines = lineAmounts.length > 0 && lineAmounts.every((amount) => roundMoney(amount) === 0);
  if (total <= 0 || itemsTotal <= 0 && !allZeroLines) {
    return lineAmounts.map(() => 0);
  }
  if (isEqualSplit(lineAmounts)) {
    const evenTotal = allocateEvenTotalTip(total, lineAmounts, itemsTotal);
    if (evenTotal) {
      return evenTotal;
    }
  }
  return allocateProportionalTip(total, lineAmounts);
}

// src/pos/workforce-printing.model.ts
function getPrinterDisplayName(printer) {
  return printer.customName?.trim() || printer.name;
}

// src/pos/area-print-rules.model.ts
import { z as z2 } from "zod";
var areaPrintRulePrinterMappingSchema = z2.object({
  printerId: z2.number().int().positive(),
  areaIds: z2.array(z2.number().int().positive())
}).strict();
var areaPrintRuleSchema = z2.object({
  id: z2.string().trim().min(1).max(120),
  name: z2.string().trim().min(1).max(120),
  productIds: z2.array(z2.number().int().positive()),
  printerMappings: z2.array(areaPrintRulePrinterMappingSchema).min(2)
}).strict();
var updateAreaPrintRulesSchema = z2.object({
  areaPrintRules: z2.array(areaPrintRuleSchema)
}).strict();

// src/pos/stream-events.model.ts
var POS_STREAM_EVENT_TYPES = [
  "order.opened",
  "order.upserted",
  "order.closed",
  "orderline.added",
  "orderline.updated",
  "orderline.item_payment_updated",
  "orderline.removed",
  "orderline.sent",
  "paymentline.created",
  "paymentline.done",
  "house-account.customer.upserted",
  "credit.tap.pending",
  "credit.tap.released",
  "credit.transaction.pending",
  "credit.transaction.resolved",
  "session.opened",
  "session.closed",
  "session.cash_alterations_updated",
  "cashDrawer.notConfigured",
  "session.daily_brief_updated",
  "session.auto_close_time_updated",
  "tenant.floor_background_updated",
  "tenant.general_item_requires_manager_approval_updated",
  "tenant.action_requires_manager_approval_updated",
  "tenant.operations_approval_requirement_updated",
  "tenant.orders_page_waiter_own_visibility_updated",
  "tenant.manual_credit_entry_blocked_updated",
  "tenant.large_table_number_on_receipt_updated",
  "tenant.stationary_name_updated",
  "tenant.open_drawer_on_partial_cash_updated",
  "tenant.open_drawer_employee_ids_updated",
  "tenant.open_drawer_requires_password_updated",
  "tenant.refund_code_via_push_updated",
  "tenant.employee_attendance_report_enabled_updated",
  "tenant.attendance_report_rules_updated",
  "area.upserted",
  "table.upserted",
  "table.removed",
  "employee.upserted",
  "employee.deactivated",
  "employee.kicked",
  "product.upserted",
  "product.deactivated",
  "payment-terminal.upserted",
  "payment-terminal.active-changed",
  "category.upserted",
  "printer.upserted",
  "happy_hour.changed",
  "variantComboGroup.upserted",
  "variantComboGroup.removed",
  "merge.links.changed",
  "approval_request.created",
  "approval_request.decided",
  "tenant.maintenance-mode-changed",
  "pos.refresh-requested",
  "deployment.refresh-requested",
  "employee.presence.probe.requested",
  "green_fire.rescue.requested",
  "employee.presence.probe.acknowledged"
];

// src/pos/past-receipts.model.ts
import { z as z3 } from "zod";
var CLOSED_ORDERS_PAGE_SIZE_MAX = 50;
var CLOSED_ORDERS_PAGE_SIZE_DEFAULT = 30;
var SESSION_FILTER_OPTIONS_LIMIT = 200;
var closedOrdersQuerySchema = z3.object({
  page: z3.coerce.number().int().min(1).default(1),
  pageSize: z3.coerce.number().int().min(1).max(CLOSED_ORDERS_PAGE_SIZE_MAX).default(CLOSED_ORDERS_PAGE_SIZE_DEFAULT),
  dateFrom: z3.string().trim().min(1).optional(),
  dateTo: z3.string().trim().min(1).optional(),
  search: z3.string().trim().min(1).max(120).optional(),
  customer: z3.string().trim().min(1).max(120).optional(),
  sessionId: z3.coerce.number().int().min(1).optional()
});

// src/pos/accountant-report.model.ts
import { z as z4 } from "zod";
var accountantReportRangeSchema = z4.object({
  from: z4.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  to: z4.string().regex(/^\d{4}-\d{2}-\d{2}$/)
}).strict().refine((value) => value.from <= value.to, { message: "from must be on or before to", path: ["from"] });
var accountantReportSessionsSchema = z4.object({
  sessionIds: z4.array(z4.number().int().positive()).min(1)
}).strict();

// src/pos/attendance-rules.model.ts
var DEFAULT_ATTENDANCE_REPORT_RULES = {
  overtime125StartHours: 8,
  overtime150StartHours: 10,
  useNightShiftOvertimeRules: false,
  overtime150Range: null,
  overtime150Weekdays: [],
  overtime200SpecialDates: []
};

// src/pos/assistant.model.ts
var ASSISTANT_QUESTION_MIN_LENGTH = 2;
var ASSISTANT_QUESTION_MAX_LENGTH = 500;
var ASSISTANT_FEEDBACK_MIN_LENGTH = 2;
var ASSISTANT_FEEDBACK_MAX_LENGTH = 2e3;
var ASSISTANT_HISTORY_MAX_TURNS = 6;
var ASSISTANT_HISTORY_TURN_MAX_LENGTH = 1e3;

// src/pos/wolt.model.ts
import { z as z5 } from "zod";
var woltOnboardingCodeSchema = z5.object({
  authorization_code: z5.string().trim().min(1).max(4096),
  redirect_url: z5.string().trim().min(1).max(2048),
  partner_venue_id: z5.string().trim().min(1).max(120)
});
var woltWebhookPayloadSchema = z5.object({
  id: z5.string().trim().min(1).max(120),
  type: z5.string().trim().min(1).max(60),
  order: z5.object({
    id: z5.string().trim().min(1).max(120),
    venue_id: z5.string().trim().max(120).optional(),
    status: z5.string().trim().min(1).max(40),
    resource_url: z5.string().trim().url().max(2048)
  }),
  created_at: z5.string().optional()
});
var woltMoneySchema = z5.object({ amount: z5.number(), currency: z5.string().optional() });
var woltOptionValueSchema = z5.object({
  name: z5.string(),
  count: z5.number().int().min(1).default(1),
  price: woltMoneySchema.optional(),
  pos_id: z5.string().nullable().optional()
});
var woltOrderItemSchema = z5.object({
  name: z5.string(),
  count: z5.number().int().min(1),
  pos_id: z5.string().nullable().optional(),
  sku: z5.string().nullable().optional(),
  options: z5.array(z5.object({ name: z5.string(), values: z5.array(woltOptionValueSchema).default([]) })).default([]),
  unit_price: woltMoneySchema.optional(),
  total_price: woltMoneySchema.optional()
});
function normalizeWoltOrderDetail(raw) {
  if (!raw || typeof raw !== "object") {
    return raw;
  }
  const order = raw;
  const items = Array.isArray(order.items) ? order.items.map((item) => {
    if (!item || typeof item !== "object") {
      return item;
    }
    const options = Array.isArray(item.options) ? item.options.map(
      (opt) => opt && typeof opt === "object" && typeof opt.value === "string" && opt.values === void 0 ? { name: opt.name, values: [{ name: opt.value, count: opt.count ?? 1, price: opt.price, pos_id: opt.value_pos_id ?? null }] } : opt
    ) : item.options;
    return {
      ...item,
      options,
      unit_price: item.unit_price ?? item.item_price?.unit_price,
      total_price: item.total_price ?? item.item_price?.total
    };
  }) : order.items;
  return { ...order, items, price: order.price ?? order.basket_price?.total };
}
var woltOrderDetailSchema = z5.preprocess(
  normalizeWoltOrderDetail,
  z5.object({
    id: z5.string(),
    order_number: z5.string().nullable().optional(),
    consumer_name: z5.string().nullable().optional(),
    consumer_phone_number: z5.string().nullable().optional(),
    consumer_comment: z5.string().nullable().optional(),
    pre_order: z5.object({ preorder_time: z5.string().nullable().optional() }).nullable().optional(),
    delivery: z5.object({ type: z5.string().optional() }).nullable().optional(),
    items: z5.array(woltOrderItemSchema).default([]),
    price: woltMoneySchema.optional()
  })
);
var woltStatusActionSchema = z5.object({
  action: z5.enum(["accept", "reject", "ready", "delivered"]),
  reason: z5.string().trim().min(3).max(200).optional()
}).refine((value) => value.action !== "reject" || Boolean(value.reason), {
  message: "reason is required for reject",
  path: ["reason"]
});
var WOLT_PHASE_BY_STATUS = {
  RECEIVED: "new",
  FETCHED: "new",
  ACKNOWLEDGED: "new",
  CREATED: "new",
  PRODUCTION: "new",
  accepted: "accepted",
  preorder_confirmed: "accepted",
  ready: "ready",
  READY: "ready",
  PICKED_UP: "ready",
  delivered: "delivered",
  DELIVERED: "delivered",
  rejected: "rejected",
  REJECTED: "rejected",
  CANCELED: "canceled"
};
var WOLT_CLAIM_STATUS_BY_ACTION = {
  accept: "accepting",
  reject: "rejecting",
  ready: "marking_ready",
  delivered: "marking_delivered"
};
var WOLT_PHASE_BY_CLAIM = {
  accepting: "new",
  rejecting: "new",
  marking_ready: "accepted",
  marking_delivered: "ready"
};
function resolveWoltPhase(status) {
  return status && (WOLT_PHASE_BY_STATUS[status] ?? WOLT_PHASE_BY_CLAIM[status]) || "unknown";
}
function isWoltClaimStatus(status) {
  return Boolean(status && WOLT_PHASE_BY_CLAIM[status]);
}
function woltSettledStatusesInPhases(phases) {
  return Object.entries(WOLT_PHASE_BY_STATUS).filter(([, phase]) => phases.includes(phase)).map(([status]) => status);
}
var woltVenueOnlineSchema = z5.object({ online: z5.boolean() });
var WOLT_PREORDER_TZ = "Asia/Jerusalem";
function formatWoltPreorderTime(iso, now = /* @__PURE__ */ new Date()) {
  if (!iso) {
    return null;
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  const day = new Intl.DateTimeFormat("en-GB", { timeZone: WOLT_PREORDER_TZ, day: "2-digit", month: "2-digit" });
  const time = new Intl.DateTimeFormat("en-GB", { timeZone: WOLT_PREORDER_TZ, hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
  const hhmm = time.format(date);
  const dayLabel = day.format(date);
  return dayLabel === day.format(now) ? hhmm : `${dayLabel.replace("/", ".")} ${hhmm}`;
}

// src/pos/z-report-layout.model.ts
import { z as z6 } from "zod";
var zReportLayoutEntryTypeSchema = z6.enum(["section", "row"]);
var zReportLayoutEntrySchema = z6.object({
  key: z6.string().trim().min(1).max(120),
  sourceKey: z6.string().trim().min(1).max(120).optional(),
  type: zReportLayoutEntryTypeSchema,
  visible: z6.boolean(),
  bold: z6.boolean().optional(),
  negative: z6.boolean().optional(),
  label: z6.string().trim().min(1).max(120)
}).strict();
var zReportLayoutConfigSchema = z6.object({
  version: z6.literal(1),
  entries: z6.array(zReportLayoutEntrySchema).max(400)
}).strict();

// src/pos/house-account.model.ts
var SYSTEM_HOUSE_ACCOUNT_LABELS = {
  wolt: "Wolt",
  tenbis: "\u05EA\u05DF \u05D1\u05D9\u05E1",
  cibus: "Cibus"
};
function systemHouseAccountLabel(systemKey) {
  return SYSTEM_HOUSE_ACCOUNT_LABELS[systemKey] ?? systemKey;
}
var SYSTEM_HOUSE_ACCOUNT_CHANNEL_NAMES = {
  wolt: "\u05D5\u05D5\u05DC\u05D8",
  tenbis: "\u05EA\u05DF \u05D1\u05D9\u05E1",
  cibus: "\u05E1\u05D9\u05D1\u05D5\u05E1"
};
function systemHouseAccountReceivableLabel(systemKey) {
  const channel = SYSTEM_HOUSE_ACCOUNT_CHANNEL_NAMES[systemKey] ?? systemHouseAccountLabel(systemKey);
  return `\u05DE\u05DE\u05EA\u05D9\u05DF \u05DC\u05EA\u05E9\u05DC\u05D5\u05DD \u05DE${channel}`;
}

// src/pos/e2e-runner.model.ts
import { z as z7 } from "zod";
var E2E_RUN_TIERS = ["gate", "full"];
var E2E_RUN_BRANCH_PATTERN = /^[\w./-]+$/;
var e2eRunStartSchema = z7.object({
  tier: z7.enum(E2E_RUN_TIERS),
  branch: z7.string().min(1).max(200).regex(E2E_RUN_BRANCH_PATTERN).optional()
});

// src/pos/session-cash-count.model.ts
import { z as z8 } from "zod";
var CASH_COUNT_BILLS = [200, 100, 50, 20];
var CASH_COUNT_COINS = [10, 5, 2, 1, 0.5, 0.1];
var CASH_COUNT_DENOMINATIONS = [...CASH_COUNT_BILLS, ...CASH_COUNT_COINS];
var CASH_COUNT_MAX_QUANTITY = 99999;
var CASH_COUNT_STATION_NAME_MAX = 50;
var quantityField = z8.number().int().min(0).max(CASH_COUNT_MAX_QUANTITY).default(0);
var cashCountQuantitiesSchema = z8.object(
  Object.fromEntries(CASH_COUNT_DENOMINATIONS.map((denomination) => [String(denomination), quantityField]))
).strict();
var upsertSessionCashCountSchema = z8.object({
  quantities: cashCountQuantitiesSchema,
  stationName: z8.string().trim().max(CASH_COUNT_STATION_NAME_MAX).nullable().optional()
}).strict();
function emptyCashCountQuantities() {
  return Object.fromEntries(CASH_COUNT_DENOMINATIONS.map((denomination) => [String(denomination), 0]));
}
function computeCashCountTotal(quantities) {
  const agorot = CASH_COUNT_DENOMINATIONS.reduce(
    (sum, denomination) => sum + Math.round(denomination * 100) * (quantities[`${denomination}`] ?? 0),
    0
  );
  return agorot / 100;
}
function expectedCashFromReport(report) {
  return roundMoney(
    report.openingCash + report.drawers.reduce(
      (sum, drawer) => sum + drawer.cashTotalWithTips + drawer.cashInTotal - drawer.cashOutTotal,
      0
    )
  );
}

export {
  MONEY_DECIMALS,
  roundMoney,
  QUICK_CHARGE_CODE_PREFIX,
  KIOSK_DINE_IN_CODE_PREFIX,
  isQuickChargeOrderCode,
  isKioskDineInOrderCode,
  WOLT_CODE_PREFIX,
  AGGREGATOR_CODE_PREFIXES,
  isAggregatorOrderCode,
  AGGREGATOR_ITEM_NOTE_PREFIX,
  AGGREGATOR_ITEM_NAME_MAX_CHARS,
  buildAggregatorItemNote,
  aggregatorItemNoteName,
  takeAwayCloseActionGroupId,
  takeAwayChannelLabel,
  MIN_ORDER_COMPLETION_NOTE_PREFIX,
  MIN_ORDER_COMPLETION_LABEL,
  isMinOrderCompletionNote,
  resolveEffectiveMinimumOrder,
  CASH_ALTERATION_ACTIONS,
  cashAlterationActionSchema,
  FAILED_CREDIT_RECOVERY_DELAY_MS,
  resolveTip,
  allocateGlobalTipShare,
  allocateGlobalTip,
  getPrinterDisplayName,
  areaPrintRulePrinterMappingSchema,
  areaPrintRuleSchema,
  updateAreaPrintRulesSchema,
  POS_STREAM_EVENT_TYPES,
  CLOSED_ORDERS_PAGE_SIZE_MAX,
  CLOSED_ORDERS_PAGE_SIZE_DEFAULT,
  SESSION_FILTER_OPTIONS_LIMIT,
  closedOrdersQuerySchema,
  accountantReportRangeSchema,
  accountantReportSessionsSchema,
  DEFAULT_ATTENDANCE_REPORT_RULES,
  ASSISTANT_QUESTION_MIN_LENGTH,
  ASSISTANT_QUESTION_MAX_LENGTH,
  ASSISTANT_FEEDBACK_MIN_LENGTH,
  ASSISTANT_FEEDBACK_MAX_LENGTH,
  ASSISTANT_HISTORY_MAX_TURNS,
  ASSISTANT_HISTORY_TURN_MAX_LENGTH,
  woltOnboardingCodeSchema,
  woltWebhookPayloadSchema,
  woltOrderDetailSchema,
  woltStatusActionSchema,
  WOLT_CLAIM_STATUS_BY_ACTION,
  resolveWoltPhase,
  isWoltClaimStatus,
  woltSettledStatusesInPhases,
  woltVenueOnlineSchema,
  formatWoltPreorderTime,
  zReportLayoutEntryTypeSchema,
  zReportLayoutEntrySchema,
  zReportLayoutConfigSchema,
  SYSTEM_HOUSE_ACCOUNT_LABELS,
  systemHouseAccountLabel,
  systemHouseAccountReceivableLabel,
  E2E_RUN_TIERS,
  E2E_RUN_BRANCH_PATTERN,
  e2eRunStartSchema,
  CASH_COUNT_BILLS,
  CASH_COUNT_COINS,
  CASH_COUNT_DENOMINATIONS,
  CASH_COUNT_MAX_QUANTITY,
  CASH_COUNT_STATION_NAME_MAX,
  cashCountQuantitiesSchema,
  upsertSessionCashCountSchema,
  emptyCashCountQuantities,
  computeCashCountTotal,
  expectedCashFromReport
};
